// Pure descriptor types for web notification categories + actions.
//
// Signal uses iOS `UNNotificationCategory` + `UNTextInputNotificationAction`
// so message banners collapse per-thread and accept inline replies. The web
// equivalent is the `Notification.actions[]` array with `type: 'text'` for
// text-input actions — available natively in Chrome, with partial support in
// Firefox/Safari.
//
// This file is *pure logic*: no `new Notification(...)`, no
// `self.registration.showNotification()`, no Solid UI. It produces descriptor
// objects that the thin Service Worker adapter translates into the system
// `NotificationOptions.actions` array. The self-test can exhaustively assert
// descriptor shape (action ids, text-input flags) without Push permission.
//
// Threat model
// ─────────────
// The peer never controls a category id or an action id. The Service Worker
// receives `event.action` from the browser (one of our registered identifiers
// or an empty string for a default tap). Parsing on the receiver side maps
// strings → `NotificationActionId` via a strict exhaustive check, so a
// corrupted/forged action-id string is silently dropped — the closed union
// type is the choke point that keeps peer-supplied bytes from steering
// action dispatch.
//
// Mirror of `kaether_for_ios/Stoat/E2EE/NotificationCategoryBuilder.swift`.

// ---------------------------------------------------------------------------
// Closed action id union — exactly 3 values.
// Adding a fourth must widen this type AND update the dispatcher switch.
// ---------------------------------------------------------------------------

export type NotificationActionId = "quickReply" | "markRead" | "mute1h";

/** All valid action ids. Used for closed-enum invariant assertions. */
export const ALL_NOTIFICATION_ACTION_IDS: readonly NotificationActionId[] = [
  "quickReply",
  "markRead",
  "mute1h",
] as const;

// ---------------------------------------------------------------------------
// Category ids (mirrors iOS `NotificationCategoryId`)
// ---------------------------------------------------------------------------

export type NotificationCategoryId = "message" | "mention" | "call";

// ---------------------------------------------------------------------------
// Descriptor shapes — browser Notification API compatible
// ---------------------------------------------------------------------------

/**
 * A single notification action descriptor.
 * Matches the browser `NotificationAction` shape:
 *   { action: string, title: string, type?: 'text', placeholder?: string }
 *
 * `inputButtonTitle` is stored separately because the web API does not have a
 * send-button label (the browser supplies its own). We keep it on the
 * descriptor for parity with the iOS spec and future use.
 */
export interface NotificationActionDescriptor {
  readonly id: NotificationActionId;
  readonly title: string;
  /** If true, action maps to `type: 'text'` in the browser Notification API. */
  readonly isTextInput: boolean;
  /**
   * iOS sends-button title. Not exposed by the web Notification API but
   * preserved for spec parity and documentation.
   */
  readonly inputButtonTitle: string | null;
  /**
   * Placeholder shown inside the text field (`placeholder` in the browser
   * Notification API). Only set when `isTextInput` is true.
   */
  readonly inputPlaceholder: string | null;
}

/** Notification category descriptor — maps to one `Notification.actions[]` set. */
export interface NotificationCategoryDescriptor {
  readonly id: NotificationCategoryId;
  readonly actions: readonly NotificationActionDescriptor[];
  /**
   * Text shown in the banner body when "Show Previews" is disabled
   * (`silent: true` equivalent on web).
   */
  readonly hiddenPreviewsBodyPlaceholder: string;
}

// ---------------------------------------------------------------------------
// Action descriptors (shared across categories)
// ---------------------------------------------------------------------------

const quickReplyAction: NotificationActionDescriptor = {
  id: "quickReply",
  title: "Reply",
  isTextInput: true,
  inputButtonTitle: "Send",
  inputPlaceholder: "Message",
};

const markReadAction: NotificationActionDescriptor = {
  id: "markRead",
  title: "Mark as read",
  isTextInput: false,
  inputButtonTitle: null,
  inputPlaceholder: null,
};

const mute1hAction: NotificationActionDescriptor = {
  id: "mute1h",
  title: "Mute for 1 hour",
  isTextInput: false,
  inputButtonTitle: null,
  inputPlaceholder: null,
};

// ---------------------------------------------------------------------------
// Category builder (mirrors iOS `NotificationCategoryBuilder`)
// ---------------------------------------------------------------------------

export const NotificationCategoryBuilder = {
  /**
   * 1:1 / channel chat banner: reply, mark-read, mute-1h.
   */
  messageCategory(): NotificationCategoryDescriptor {
    return {
      id: "message",
      actions: [quickReplyAction, markReadAction, mute1hAction],
      hiddenPreviewsBodyPlaceholder: "New message",
    };
  },

  /**
   * @-mention banner. Reuses the message action set; only the category id
   * differs so the Service Worker can route it to mention-specific UI.
   */
  mentionCategory(): NotificationCategoryDescriptor {
    return {
      id: "mention",
      actions: [quickReplyAction, markReadAction, mute1hAction],
      hiddenPreviewsBodyPlaceholder: "You were mentioned",
    };
  },

  /**
   * Incoming call. Inline text reply makes no sense; the call-ringing UI
   * owns accept/decline. We register the category so call pushes can carry
   * it and the browser knows to apply call-style presentation rules.
   */
  callCategory(): NotificationCategoryDescriptor {
    return {
      id: "call",
      actions: [],
      hiddenPreviewsBodyPlaceholder: "Incoming call",
    };
  },

  /**
   * One-shot bundle for registration at Service Worker install. Order does
   * not matter — categories are keyed by id.
   */
  allCategories(): NotificationCategoryDescriptor[] {
    return [
      NotificationCategoryBuilder.messageCategory(),
      NotificationCategoryBuilder.mentionCategory(),
      NotificationCategoryBuilder.callCategory(),
    ];
  },

  /**
   * Convert a descriptor to the browser `NotificationAction` shape
   * (`{ action, title, type?, placeholder? }`). The caller passes the
   * result array as `NotificationOptions.actions`.
   *
   * Pure function — no API calls.
   */
  toBrowserAction(d: NotificationActionDescriptor): {
    action: string;
    title: string;
    type?: "text";
    placeholder?: string;
  } {
    const base: { action: string; title: string; type?: "text"; placeholder?: string } = {
      action: d.id,
      title: d.title,
    };
    if (d.isTextInput) {
      base.type = "text";
      if (d.inputPlaceholder) {
        base.placeholder = d.inputPlaceholder;
      }
    }
    return base;
  },
} as const;
